import { action, observable, makeObservable } from 'mobx';
import { SellerEditForm } from 'modules/seller/forms';
import { BaseViewStore } from 'core/stores';
import { ApplicationStore } from 'platform/application/stores';
import { formInvalidate, isSome, generateRandomString } from 'core/utils';
import { LookupStore } from 'modules/lookup/stores';
import { lookupRouteName, organizationTypes, sourceOfChange } from 'common/constants';
import _ from 'lodash';
import { SellerEdit3PLCredentials, SellerWarehouse } from '../pages';
import { BaasicDropdownViewStore } from 'common/stores';
import { CarrierService } from 'common/data';
import { carrierTypes } from 'common/constants';
import { CarrierStore } from 'modules/carrier/stores';

class SellerEditViewStore extends BaseViewStore {
	isUpdateAction = isSome(this.rootStore.routerStore.routerState.params.id);
	organizationTypeId = null;
	@observable infiniteSearchPhrase = null;
	@observable warehouseManagements = [];
	warehouses = [];
	@observable isHavingAPIKey = false;

	form = new SellerEditForm({
		onSuccess: async () => {
			this.createOrUpdateApplication();
		},
	});

	constructor(rootStore) {
		super(rootStore);
		makeObservable(this);
		this.listStore = new ApplicationStore(rootStore);
		this.lookupStore = new LookupStore(rootStore);
		this.applicationStore = new ApplicationStore(rootStore);
		this.initialize(rootStore);
	}

	@action async initialize() {
		this.suspend();
		this.form.reset();
		await this.initializeDropdowns();
		await this.getWarehouses();
		if (this.isUpdateAction) {
			await this.getApplication(this.rootStore.routerStore.routerState.params.id);
			if (this.listStore.item.apiKey != '') this.isHavingAPIKey = true;
			const additionalInfo = this.listStore.item.sellerOrganizationInfo;
			this.listStore.item.address = additionalInfo.address;
			this.listStore.item.contact = additionalInfo.contact;
			this.listStore.item.minimumInventory = additionalInfo.minimumInventory;
			this.listStore.item.defaultSafeQuantity = additionalInfo.defaultSafeQuantity;
			this.listStore.item.priceLtl = additionalInfo.priceLtl;
			this.listStore.item.useAmazonInventoryMerge = !!additionalInfo.useAmazonInventoryMerge;
			this.listStore.item.useSameCarrierPerOrder = !!additionalInfo.useSameCarrierPerOrder;
			this.form.updateForm(this.listStore.item, true);
			if (additionalInfo && additionalInfo.address && additionalInfo.address.coreCountry) {
				this.countryDropdownStore.setValue(additionalInfo.address.coreCountry);
			}
			if (additionalInfo && additionalInfo.address && additionalInfo.address.coreCountryState) {
				this.stateDropdownStore.setValue(additionalInfo.address.coreCountryState);
			}
			if (this.listStore.item.timezone) {
				this.timezoneDropdownStore.setValue(this.listStore.item.timezone);
			}
			if (this.listStore.item.id) {
				this.defaultLtlCarrierDropdownStore.updateFilter({
					sellerIds: [
						this.listStore.item.id
					],
				});
			}
			if (additionalInfo.defaultLtlCarrier) {
				this.defaultLtlCarrierDropdownStore.setValue(additionalInfo.defaultLtlCarrier);
			}
		} else {
			this.form.$('email').set('rules', 'required|email|string');
			this.form.$('password').set('rules', 'required|string|password');
			this.form.$('confirmPassword').set('rules', 'required|string|same:password');
		}
		this.setOrganizationTypeId();
		this.resume();
	}

	@action.bound async setOrganizationTypeId() {
		await this.lookupStore.find({ page: 1, rpp: 250 }, lookupRouteName.organizationType);
		this.organizationTypeId = _.find(this.lookupStore.data, organizationType => {
			return organizationType.abrv.toUpperCase() === organizationTypes.seller.toUpperCase();
		}).id;
	}

	@action.bound
	initializeDropdowns = async () => {
		this.countryDropdownStore = new BaasicDropdownViewStore({
			field: this.form.$('countryId'),
			store: new LookupStore(this.rootStore),
			lookupRoute: lookupRouteName.country,
		});
		this.stateDropdownStore = new BaasicDropdownViewStore({
			field: this.form.$('stateId'),
			store: new LookupStore(this.rootStore),
			lookupRoute: lookupRouteName.state,
		});
		this.timezoneDropdownStore = new BaasicDropdownViewStore({
			field: this.form.$('timezoneId'),
			store: new LookupStore(this.rootStore),
			lookupRoute: lookupRouteName.timezone,
		});

		this.defaultLtlCarrierDropdownStore = new BaasicDropdownViewStore();
		await this.defaultLtlCarrierDropdownStore.initialize({
			field: this.form.$('defaultLtlCarrierId'),
			store: new CarrierStore(this.rootStore),
			itemTemplate: {
				value: 'id',
				label: 'name'
			},
			fetchFunc: 'findCarriers',
			fetchInitialValues: false,
			filterProperties: {
				carrierTypeNames: [
					carrierTypes.ltl
				]
			},
			staticOptions: null
		});
	};

	@action.bound
	getWarehouses = async () => {
		this.warehouses = (await this.applicationStore.find({
			embed: ['warehouseOrganizationInfo'],
			rpp: 100,
			page: 1,
			isOrganizationActive: true,
			warehouseOnly: true,
		})).item;
		await this.prepareData();
	};

	@action.bound
	prepareData = async () => {
		_.each(this.warehouses, warehouse => {
			this.warehouseManagements.push({
				warehouseName: warehouse.identifier,
				customerId: null,
				clientId: null,
				clientSecret: null,
				warehouseOrganizationInfoId: _.get(warehouse, 'warehouseOrganizationInfo.id'),
				warehouseOrganizationInfo: _.get(warehouse, 'warehouseOrganizationInfo'),
			});
		});
	};

	@action async getApplication(id) {
		await this.listStore.getApplication(id, {
			embed: [
				'timezone',
				'sellerOrganizationInfo',
				'sellerOrganizationInfo.address',
				'sellerOrganizationInfo.address.coreCountry',
				'sellerOrganizationInfo.address.coreCountryState',
				'sellerOrganizationInfo.contact',
				'sellerOrganizationInfo.defaultLtlCarrier',
			],
		});
	}

	@action.bound
	async onMenuClose(routeName) {
		this.infiniteSearchPhrase = null;
		await this.getLookupOptions(routeName);
	}

	@action async createOrUpdateApplication() {
		this.suspend();
		const {
			identifier,
			applicationTitle,
			address: { address1, address2, address3, city, zipCode },
			minimumInventory,
			countryId,
			stateId,
			defaultSafeQuantity,
			defaultLtlCarrierId,
			priceLtl,
			useAmazonInventoryMerge,
			useSameCarrierPerOrder,
			timezoneId,
			contact,
			email,
			password,
			confirmPassword,
			apiKey,
		} = this.form.values();

		let addressObj = {
			address1,
			address2,
			address3,
			city,
			zipCode,
			coreCountryStateId: stateId,
			coreCountryId: countryId,
		};

		var organization = {
			identifier,
			applicationTitle,
			organizationTypeId: this.organizationTypeId,
			apiKey,
			isActive: this.isUpdateAction ? this.listStore.item.isActive : true,
			timezoneId,
		};

		var organizationInfo = {
			minimumInventory,
			printPackingSlip: this.isUpdateAction ? this.listStore.item.sellerOrganizationInfo.printPackingSlip : true,
			partiallyFulfillMixedOrders: this.isUpdateAction
				? this.listStore.item.sellerOrganizationInfo.partiallyFulfillMixedOrders
				: true,
			markShippedInWms: this.isUpdateAction
				? this.listStore.item.sellerOrganizationInfo.markShippedInWms
				: false,
			address: addressObj,
			contact,
			defaultLtlCarrierId: defaultLtlCarrierId,
			defaultSafeQuantity: defaultSafeQuantity,
			priceLtl: priceLtl,
			useSameCarrierPerOrder: useSameCarrierPerOrder,
		};

		if (!this.isUpdateAction) {
			organization = {
				...organization,

				membershipData: {
					email,
					password,
				},

				coreUserInfo: {
					piiAccess: true,
				},

				confirmPassword,
			};
		} else {
			const additionalInfo = this.listStore.item.sellerOrganizationInfo;
			organizationInfo = {
				...organizationInfo,
				id: additionalInfo.id,
				addressId: additionalInfo.address.id,
				contactId: additionalInfo.contact.id,
				showSurplusCharge: additionalInfo.showSurplusCharge,
				useAmazonInventoryMerge: useAmazonInventoryMerge,
			};
		}

		organization.sellerOrganizationInfo = organizationInfo;
		if (
			!this.isUpdateAction &&
			password
				.trim()
				.toLowerCase()
				.includes(
					email
						.split('@')[0]
						.toLowerCase()
						.trim()
				)
		) {
			this.form.invalidate('PASSWORDCONTAINSUNSERNAME');
		} else {
			try {
				if (this.isUpdateAction) {
					this.listStore.updateApplication(
						{ id: this.rootStore.routerStore.routerState.params.id, ...organization },
						sourceOfChange.editSeller
					);
				} else {
					organization.warehouseManagements = this.warehouseManagements;
					await this.listStore.createApplication(organization, sourceOfChange.createSeller);
					this.rootStore.notificationStore.warning(['SELLERVIEW.ADDREQUIREDCARRIER']);
					this.rootStore.notificationStore.warning(['SELLERVIEW.ADDSELLERWAREHOUSES']);
					this.rootStore.viewStore.sellerDropdownStore.fetchOptions();
				}
				this.rootStore.notificationStore.success(
					this.isUpdateAction
						? ['LOOKUPUPDATE.SELLER', { seller: organization.identifier }]
						: ['LOOKUPCREATE.SELLER', { seller: organization.identifier }]
				);
				this.rootStore.routerStore.goTo('master.app.main.seller.list');
			} catch (err) {
				formInvalidate({ form: this.form, err });
			}
		}
		this.resume();
	}

	@action.bound generateApiKey() {
		this.isHavingAPIKey = true;
		var radnomStringGenerated = generateRandomString(32);
		this.form.$('apiKey').set('value', radnomStringGenerated);
	}

	openEditSeller3PLCredentialsModal = () => {
		this.openModal({
			data: { parentView: this, selectedItem: this.listStore.item },
			component: SellerEdit3PLCredentials,
			className: 'ReactModal--lrg',
		});
	};

	openEditSellerWarehousesModal = () => {
		this.openModal({
			data: { ...this, selectedItem: this.listStore.item },
			component: SellerWarehouse,
			className: 'ReactModal--sml-med',
		});
	};
}

export default SellerEditViewStore;
